import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useState
} from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import { useImmer } from 'use-immer'

import useAuth from '@hook/shared/use-auth'

import { parseParametrizatioReabCronogramaExport } from '@helper/grid/parametrization-curve'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrizationCurves from '@service/parametrization-curves'
import parametrizationReab from '@service/parametrization-reab'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import type {
  IParams,
  IParamsEdit,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate
} from '@base/@types/providers/Parametrization/CurveWeightProvider'
import { IReabTimelineContext } from '@base/@types/providers/Parametrization/ReabTimelneProvider'
import parametrizationReabTimelineHeaders from '@base/variables/headers/parametrization/reab-cronograma-export'

interface ReabTimelineProviderProps {
  children: ReactNode
}

const ReabTimelineProvider: FC<ReabTimelineProviderProps> = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [loadingHistoric, setLoadingHistoric] = useState(false)
  const [filters, setFilters] = useBoolean(false)
  const [isChange, setIsChange] = useState(false)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('REAB Cronograma - Relatório')
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const [_error] = useState<string | undefined>()
  const [loadingEdit] = useState(false)
  const [pagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [data, updateData] = useImmer<any[]>([])
  const [historic, updateHistoric] = useImmer<any[]>([])
  const [chapariaTimelineDefault, updateChapariaTimelineDefault] =
    useImmer<any>({
      Segunda: [],
      Terça: [],
      Quarta: [],
      Quinta: [],
      Sexta: []
    })
  const [chapariaTimelineSpecial, updateChapariaTimelineSpecial] =
    useImmer<any>({
      Segunda: [],
      Terça: [],
      Quarta: [],
      Quinta: [],
      Sexta: []
    })
  const [chapariaTimelineDefaultNew, handleTimelineChapariaNormalyNew] =
    useImmer<any>({})
  const [chapariaTimelineSpecialNew, handleTimelineChapariaSpecialNew] =
    useImmer<any>({})

  const [ferragemTimelineDefault, updateFerragemTimelineDefault] =
    useImmer<any>({
      Segunda: [],
      Terça: [],
      Quarta: [],
      Quinta: [],
      Sexta: []
    })
  const [ferragemTimelineSpecial, updateFerragemTimelineSpecial] =
    useImmer<any>({
      Segunda: [],
      Terça: [],
      Quarta: [],
      Quinta: [],
      Sexta: []
    })
  const [ferragemTimelineDefaultNew, handleTimelineFerragemNormalyNew] =
    useImmer<any>({})
  const [ferragemTimelineSpecialNew, handleTimelineFerragemSpecialNew] =
    useImmer<any>({})
  const [selected, setSelectedOne] = useState(0)
  const [activeTimelineChaparia, handleTimelineActiveChaparia] = useState(0)
  const [activeTimelineFerragem, handleTimelineActiveFerragem] = useState(0)
  const { companies: authCompanies } = useAuth()
  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [filterEdit] = useImmer({
    centers: [],
    products: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const [companieId, setCompanieId] = useState('')
  const [selectedLojaId, setSelectedLojaId] = useState<string>('')
  const [filteredData, setFilteredData] = useState<any[]>([])
  const [originalData, setOriginalData] = useState<any[]>([])

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() + 7)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(new Date()).format('YYYY-MM-DD'),
    dateEnd: dayjs(dateEnd).format('YYYY-MM-DD')
  })
  const [paramsDatesFerragem, updateDatesParamsFerragem] = useImmer({
    dateInit: dayjs(new Date()).format('YYYY-MM-DD'),
    dateEnd: dayjs(dateEnd).format('YYYY-MM-DD')
  })

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }
  const handleParamsDateFerragem = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParamsFerragem(draft => {
      draft[name] = value
    })
  }

  // Função para obter lojas distintas dos dados
  const getDistinctLojas = useCallback((data: any[]) => {
    const lojas = [...new Set(data.map(item => item.lojaId))]
      .filter(lojaId => lojaId) // Remove valores null/undefined
      .map(lojaId => ({
        value: lojaId.toString(),
        label: `Loja ${lojaId}`
      }))
      .sort((a, b) => parseInt(a.value) - parseInt(b.value))

    return [{ value: '', label: 'Todas as lojas' }, ...lojas]
  }, [])

  // Função para reconstruir os calendários com dados filtrados
  const rebuildCalendars = useCallback(
    (data: any[]) => {
      // Chaparia
      const chapariaDefault = {
        Segunda: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 1 && x.setorId === 1
        ),
        Terça: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 2 && x.setorId === 1
        ),
        Quarta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 3 && x.setorId === 1
        ),
        Quinta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 4 && x.setorId === 1
        ),
        Sexta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 5 && x.setorId === 1
        )
      }

      const chapariaSpecial = {
        Segunda: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 1 && x.setorId === 1
        ),
        Terça: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 2 && x.setorId === 1
        ),
        Quarta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 3 && x.setorId === 1
        ),
        Quinta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 4 && x.setorId === 1
        ),
        Sexta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 5 && x.setorId === 1
        )
      }

      // Ferragem
      const ferragemDefault = {
        Segunda: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 1 && x.setorId === 2
        ),
        Terça: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 2 && x.setorId === 2
        ),
        Quarta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 3 && x.setorId === 2
        ),
        Quinta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 4 && x.setorId === 2
        ),
        Sexta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 1 && x.diaDaSemana === 5 && x.setorId === 2
        )
      }

      const ferragemSpecial = {
        Segunda: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 1 && x.setorId === 2
        ),
        Terça: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 2 && x.setorId === 2
        ),
        Quarta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 3 && x.setorId === 2
        ),
        Quinta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 4 && x.setorId === 2
        ),
        Sexta: data?.filter(
          (x: any) =>
            x.tipoCronograma === 2 && x.diaDaSemana === 5 && x.setorId === 2
        )
      }

      // Atualiza os calendários

      updateChapariaTimelineDefault(chapariaDefault)
      updateChapariaTimelineSpecial(chapariaSpecial)
      handleTimelineChapariaNormalyNew(chapariaDefault)
      handleTimelineChapariaSpecialNew(chapariaSpecial)

      updateFerragemTimelineDefault(ferragemDefault)
      updateFerragemTimelineSpecial(ferragemSpecial)
      handleTimelineFerragemNormalyNew(ferragemDefault)
      handleTimelineFerragemSpecialNew(ferragemSpecial)
    },
    [
      updateChapariaTimelineDefault,
      updateChapariaTimelineSpecial,
      handleTimelineChapariaNormalyNew,
      handleTimelineChapariaSpecialNew,
      updateFerragemTimelineDefault,
      updateFerragemTimelineSpecial,
      handleTimelineFerragemNormalyNew,
      handleTimelineFerragemSpecialNew
    ]
  )

  // Função para filtrar dados por loja
  const handleLojaFilter = useCallback(
    (lojaId: string) => {
      setSelectedLojaId(lojaId)

      if (!lojaId || lojaId === '') {
        // Se não há filtro, mostra todos os dados e recria os calendários
        setFilteredData(originalData)
        updateData(originalData)
        rebuildCalendars(originalData)
      } else {
        // Filtra por lojaId
        const filtered = originalData.filter(item => {
          return item.lojaId.toString() === lojaId
        })

        setFilteredData(filtered)
        updateData(filtered)

        if (filtered.length > 0) {
          // Só reconstrói os calendários se houver dados filtrados
          rebuildCalendars(filtered)
        } else {
        }
        // Se não há dados filtrados, mantém os calendários como estão
      }
    },
    [originalData, updateData, rebuildCalendars]
  )

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    id: 0,
    curva: '',
    pesoLoja: 0,
    pesoEmpresa: 0,
    pesoVolume: 0,
    pesoCurva: 0,
    corteInicial: 0,
    corteFinal: 0
  })

  const handleUpdateEditParams = useCallback(
    async ({ name, value }: IUpdateParamsEdit) => {
      updateEditParams(draft => {
        draft[name] = value
      })
    },
    [updateEditParams]
  )

  const getCenters = useCallback(async () => {}, [])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    produtoSemFornecedor: false
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationReabTimelineHeaders
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationHeaderReabTimeline')) {
      encrypteHeaderStorange('parametrizationHeaderReabTimeline', headers)
    } else {
      localStorage.removeItem('parametrizationHeaderReabTimeline')
      encrypteHeaderStorange('parametrizationHeaderReabTimeline', headers)
    }
  }, [headers])

  const exportData = useCallback(async () => {}, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationHeaderReabTimeline')) {
      const newHeader = decrypterHeaderStorange(
        'parametrizationHeaderReabTimeline'
      )
      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      if (name === 'curvaIgualZero') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
      if (name === 'produtoSemFornecedor') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
    })
  }

  const handlePageChange = useCallback(async (_page: number) => {
    setLoading(true)
  }, [])

  const getData = useCallback(
    async (id: any) => {
      setLoading(true)
      // await getMaintence()

      encrypteHeaderStorange('parametrizationHeaderReabTimeline', headers)

      handleStorange()

      try {
        parametrizationReab
          .getParamsReabCronograma(id)
          .then(({ data: { data } }) => {
            // Armazena dados originais
            setOriginalData(data)
            updateData(data)

            // Se há um filtro de loja selecionado, aplica o filtro
            if (selectedLojaId && selectedLojaId !== '') {
              const filtered = data.filter(
                (item: { lojaId: { toString: () => string } }) =>
                  item.lojaId.toString() === selectedLojaId
              )
              setFilteredData(filtered)
              updateData(filtered)
              if (filtered.length > 0) {
                rebuildCalendars(filtered)
              } else {
                rebuildCalendars(data) // Mantém estrutura original se não há dados filtrados
              }
            } else {
              setFilteredData(data)
              rebuildCalendars(data)
            }

            // Configura timeline ativo para chaparia
            const activeTimelineChapariaRet =
              data?.find(
                (x: any) =>
                  x.tipoCronograma === 2 && x.ativo === true && x.setorId === 1
              ) !== undefined
                ? 1
                : 0

            // Configura timeline ativo para ferragem
            const activeTimelineFerragemRet =
              data?.find(
                (x: any) =>
                  x.tipoCronograma === 2 && x.ativo === true && x.setorId === 2
              ) !== undefined
                ? 1
                : 0

            handleTimelineActiveChaparia(activeTimelineChapariaRet)
            handleTimelineActiveFerragem(activeTimelineFerragemRet)

            // Configura datas especiais
            const dateTimeChapariaSpecial = data?.find(
              (x: any) => x.tipoCronograma === 2 && x.setorId === 1
            )
            if (dateTimeChapariaSpecial !== undefined) {
              updateDatesParams({
                dateInit: dayjs(dateTimeChapariaSpecial.dataInicio).format(
                  'YYYY-MM-DD'
                ),
                dateEnd: dayjs(dateTimeChapariaSpecial.dataFim).format(
                  'YYYY-MM-DD'
                )
              })
            }

            const dateTimeFerragemSpecial = data?.find(
              (x: any) => x.tipoCronograma === 2 && x.setorId === 2
            )
            if (dateTimeFerragemSpecial !== undefined) {
              updateDatesParamsFerragem({
                dateInit: dayjs(dateTimeFerragemSpecial.dataInicio).format(
                  'YYYY-MM-DD'
                ),
                dateEnd: dayjs(dateTimeFerragemSpecial.dataFim).format(
                  'YYYY-MM-DD'
                )
              })
            }

            setLoading(false)
          })
          .catch(() => {
            setOriginalData([])
            setFilteredData([])
            updateData([])
            handleTimelineActiveChaparia(0)
            updateChapariaTimelineDefault({
              Segunda: [],
              Terça: [],
              Quarta: [],
              Quinta: [],
              Sexta: []
            })
            updateChapariaTimelineSpecial({
              Segunda: [],
              Terça: [],
              Quarta: [],
              Quinta: [],
              Sexta: []
            })
            handleTimelineChapariaNormalyNew({
              Segunda: [],
              Terça: [],
              Quarta: [],
              Quinta: [],
              Sexta: []
            })
            handleTimelineChapariaSpecialNew({
              Segunda: [],
              Terça: [],
              Quarta: [],
              Quinta: [],
              Sexta: []
            })
            handleTimelineActiveFerragem(0)
            updateFerragemTimelineDefault({
              Segunda: [],
              Terça: [],
              Quarta: [],
              Quinta: [],
              Sexta: []
            })
            updateFerragemTimelineSpecial({
              Segunda: [],
              Terça: [],
              Quarta: [],
              Quinta: [],
              Sexta: []
            })
            handleTimelineFerragemNormalyNew({
              Segunda: [],
              Terça: [],
              Quarta: [],
              Quinta: [],
              Sexta: []
            })
            handleTimelineFerragemSpecialNew({
              Segunda: [],
              Terça: [],
              Quarta: [],
              Quinta: [],
              Sexta: []
            })
            setLoading(false)
          })
      } catch {
        setLoading(false)
      }
    },
    [
      headers,
      handleStorange,
      updateData,
      updateChapariaTimelineDefault,
      updateChapariaTimelineSpecial,
      handleTimelineChapariaNormalyNew,
      handleTimelineChapariaSpecialNew,
      updateFerragemTimelineDefault,
      updateFerragemTimelineSpecial,
      handleTimelineFerragemNormalyNew,
      handleTimelineFerragemSpecialNew,
      updateDatesParams,
      updateDatesParamsFerragem,
      selectedLojaId,
      rebuildCalendars
    ]
  )

  const updateDataCurve = useCallback(async () => {
    setLoading(true)

    try {
      const { data: _res } = await parametrizationCurves.postParamsCurveWeight({
        id: paramsEdit.id,
        curva: paramsEdit.curva,
        pesoCurvaLoja: Number(paramsEdit.pesoLoja),
        pesoCurvaEmpresa: Number(paramsEdit.pesoEmpresa),
        pesoCurvaVolume: Number(paramsEdit.pesoVolume),
        pesoCurva: Number(paramsEdit.pesoCurva),
        corteInicial: Number(paramsEdit.corteInicial),
        corteFinal: Number(paramsEdit.corteFinal)
      })
      toast({
        title: 'Sucesso.',
        description: 'Curva Peso editado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData(companieId)
  }, [
    getData,
    companieId,
    paramsEdit.id,
    paramsEdit.curva,
    paramsEdit.pesoLoja,
    paramsEdit.pesoEmpresa,
    paramsEdit.pesoVolume,
    paramsEdit.pesoCurva,
    paramsEdit.corteInicial,
    paramsEdit.corteFinal,
    toast
  ])

  const deleteData = useCallback(async () => {
    setLoading(true)

    try {
      await parametrizationCurves.removeParamsCurveWeight({
        ids: [Number(selected)]
      })

      toast({
        title: 'Sucesso.',
        description: 'Registros deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData(companieId)
  }, [getData, companieId, selected, toast])

  const createData = useCallback(async () => {
    setLoading(true)
    const arrayUnicoChapariaDefault = Object.values(
      chapariaTimelineDefaultNew
    ).flat()
    const arrayUnicoChapariaSpecial = Object.values(
      chapariaTimelineSpecialNew
    ).flat()
    const mergedArray = arrayUnicoChapariaDefault.concat(
      arrayUnicoChapariaSpecial
    )
    const arrayUnicoFerragemDefault = Object.values(
      ferragemTimelineDefaultNew
    ).flat()
    const arrayUnicoFerragemSpecial = Object.values(
      ferragemTimelineSpecialNew
    ).flat()

    const mergedArrayFerragem = arrayUnicoFerragemDefault.concat(
      arrayUnicoFerragemSpecial
    )
    // eslint-disable-next-line prefer-const

    // eslint-disable-next-line prefer-const
    let cronogramaReabDefault: any = []

    mergedArray.map((item: any) => {
      const obj = {
        centroDistribuicaoId: companieId === '' ? authCompanies[0] : companieId,
        tipoCronograma: item.tipoCronograma,
        setorId: 1,
        lojaId: Number(item.lojaId),
        diaDaSemana: item.diaDaSemana,
        ativo:
          (activeTimelineChaparia === 0 && item.tipoCronograma === 1) ||
          (activeTimelineChaparia === 1 && item.tipoCronograma === 2),
        ...(item.dataInicio !== undefined && {
          dataInicio: paramsDates.dateInit
        }),
        ...(item.dataFim !== undefined && { dataFim: paramsDates.dateEnd })
      }

      cronogramaReabDefault.push(obj)
    })

    mergedArrayFerragem.map((item: any) => {
      const obj = {
        centroDistribuicaoId: companieId === '' ? authCompanies[0] : companieId,
        tipoCronograma: item.tipoCronograma,
        setorId: 2,
        lojaId: Number(item.lojaId),
        diaDaSemana: item.diaDaSemana,
        ativo:
          (activeTimelineFerragem === 0 && item.tipoCronograma === 1) ||
          (activeTimelineFerragem === 1 && item.tipoCronograma === 2),
        ...(item.dataInicio !== undefined && {
          dataInicio: paramsDatesFerragem.dateInit
        }),
        ...(item.dataFim !== undefined && {
          dataFim: paramsDatesFerragem.dateEnd
        })
      }

      cronogramaReabDefault.push(obj)
    })

    try {
      await parametrizationReab.putParamsReabCronograma({
        centroDistribuicaoId: companieId === '' ? authCompanies[0] : companieId,
        cronogramaReab: cronogramaReabDefault
      })
      getData(companieId === '' ? authCompanies[0] : companieId)
      toast({
        title: 'Sucesso.',
        description: 'Registros Atualizado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      setIsChange(false)
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
      setIsChange(false)
    }
    setSelectedOne(0)
  }, [
    chapariaTimelineDefaultNew,
    chapariaTimelineSpecialNew,
    ferragemTimelineDefaultNew,
    ferragemTimelineSpecialNew,
    getData,
    companieId,
    authCompanies,
    activeTimelineChaparia,
    paramsDates.dateInit,
    paramsDates.dateEnd,
    activeTimelineFerragem,
    paramsDatesFerragem.dateInit,
    paramsDatesFerragem.dateEnd,
    toast
  ])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return []
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'paramasReabTimeline',
            data,
            headers,
            parser: parseParametrizatioReabCronogramaExport
          }
        ]
      },
      {
        type: 'reabTimelineHistoric',
        data: data,
        onUpdate: updateData
      }
    ]
  }, [data, headers, updateData])

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'id',
          value: idData[0].id
        })
        handleUpdateEditParams({
          name: 'curva',
          value: idData[0].curva
        })
        handleUpdateEditParams({
          name: 'pesoLoja',
          value: idData[0].pesoCurvaLoja
        })
        handleUpdateEditParams({
          name: 'pesoEmpresa',
          value: idData[0].pesoCurvaEmpresa
        })
        handleUpdateEditParams({
          name: 'pesoVolume',
          value: idData[0].pesoCurvaVolume
        })
        handleUpdateEditParams({
          name: 'pesoCurva',
          value: idData[0].pesoCurva
        })
        handleUpdateEditParams({
          name: 'corteInicial',
          value: idData[0].corteInicial
        })
        handleUpdateEditParams({
          name: 'corteFinal',
          value: idData[0].corteFinal
        })
      }
    },
    [handleUpdateEditParams, data]
  )

  const handleSelect = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selected

      setSelectedOne(prev => (prev !== similarProduct ? similarProduct : 0))
      if (newSelected === similarProduct) {
        updateEditParams({
          id: 0,
          curva: 0,
          pesoLoja: 0,
          pesoEmpresa: 0,
          pesoVolume: 0,
          pesoCurva: 0,
          corteInicial: 0,
          corteFinal: 0
        })
      } else {
        handleEditParams(index['id-id'])
      }
    },
    [setSelectedOne, handleEditParams, selected, updateEditParams]
  )

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        onChange: handleSelect,
        selecteds: selected,
        isMulti: false
      }
    ]
  }, [selected, handleSelect])

  const handleCompanieId = useCallback(
    async (companieId: any) => {
      setCompanieId(companieId.value)
      getData(companieId.value)
    },
    [getData]
  )

  const getHistoric = useCallback(
    async (id: any) => {
      setLoadingHistoric(true)

      try {
        await parametrizationReab
          .getParamsReabCronogramaHistoric(id)
          .then(({ data: { data } }) => {
            updateHistoric(data)
            setLoadingHistoric(false)
          })
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setLoadingHistoric(false)
      }
    },
    [updateHistoric, toast]
  )
  return (
    <ReabTimelineContext.Provider
      value={{
        loading,
        loadingEdit,
        data: parseParametrizatioReabCronogramaExport(data),
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selected,
        filterEdit,
        paramsEdit,
        paramsDates,
        tooglesTable,
        filename,
        query,
        historic,
        filters,
        hiddenColumns,
        handlePageChange,
        pagination,
        fastQuery,
        filtersList,
        toggleColumn,
        handleUpdateParams,
        handleSelect,
        getCenters,
        handleParamsDate,
        setQuery,
        getData,
        exportData,
        createData,
        handleUpdateEditParams,
        handleToogleTable,

        updateDataCurve,
        deleteData,
        setFilters,
        setFilename,
        companieId,
        handleTimelineChapariaSpecialNew,
        handleCompanieId,
        chapariaTimelineDefault,
        chapariaTimelineSpecial,
        activeTimelineChaparia,
        handleTimelineActiveChaparia,
        handleTimelineChapariaNormalyNew,
        ferragemTimelineDefault,
        ferragemTimelineSpecial,
        activeTimelineFerragem,
        handleTimelineActiveFerragem,
        handleTimelineFerragemNormalyNew,
        handleTimelineFerragemSpecialNew,
        isChange,
        loadingHistoric,
        getHistoric,
        setIsChange,
        handleParamsDateFerragem,
        paramsDatesFerragem,
        selectedLojaId,
        getDistinctLojas,
        handleLojaFilter,
        originalData
      }}
    >
      {children}
    </ReabTimelineContext.Provider>
  )
}

export default ReabTimelineProvider
export const ReabTimelineContext = createContext<IReabTimelineContext>(
  {} as IReabTimelineContext
)
